import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import type {
	CategoryEntryPointExtraProps,
	CategoryEntryPointParams,
	CategoryEntryPointRuntimeProps,
} from '../../types';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- Relay-generated artifacts require the .graphql suffix.
import codingAgentsQuery, {
	type CodingAgentListInstalledAgentsQuery,
} from '../../ui/CodingAgentList/__generated__/CodingAgentListInstalledAgentsQuery.graphql';
import { RECOMMENDED_SLOT_COUNT } from '../../ui/RecommendedList/buildRecommendedItems';
import { resolveProduct } from '../../utils/resolveProduct';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- Relay-generated .graphql.ts artifact; the `.graphql` suffix is required to resolve it
import LegacyRecommendedAgentResultsQuery from './__generated__/LegacyRecommendedAgentResultsQuery.graphql';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- Relay-generated .graphql.ts artifact; the `.graphql` suffix is required to resolve it
import type { LegacyRecommendedAgentResultsQuery as LegacyRecommendedAgentResultsQueryType } from './__generated__/LegacyRecommendedAgentResultsQuery.graphql';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- Relay-generated .graphql.ts artifact; the `.graphql` suffix is required to resolve it
import RecommendedAgentResultsQuery from './__generated__/RecommendedAgentResultsQuery.graphql';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- Relay-generated .graphql.ts artifact; the `.graphql` suffix is required to resolve it
import type { RecommendedAgentResultsQuery as RecommendedAgentResultsQueryType } from './__generated__/RecommendedAgentResultsQuery.graphql';
import { OOTB_AGENTS_BY_PRODUCT } from './constants';
import { getRecentAgents } from './recentAgents';
import { resolveTemplateProduct } from './resolveTemplateProduct';

const TEMPLATE_PAGE_SIZE = 50;
const UPLIFT_TEMPLATE_PAGE_SIZE = 4;

type RecommendedAgentResultsEntryPointComponent = EntryPointComponent<
	| {
			recommendedAgentResultsQuery: RecommendedAgentResultsQueryType;
			codingAgentsQuery: CodingAgentListInstalledAgentsQuery;
	  }
	| {
			legacyRecommendedAgentResultsQuery: LegacyRecommendedAgentResultsQueryType;
	  },
	Record<string, never>,
	CategoryEntryPointRuntimeProps,
	CategoryEntryPointExtraProps
>;

export const recommendedAgentResultsEntryPoint: EntryPoint<
	RecommendedAgentResultsEntryPointComponent,
	CategoryEntryPointParams
> = createEntryPoint({
	root: JSResourceForInteraction<RecommendedAgentResultsEntryPointComponent>(() => import(/* webpackChunkName: "@atlaskit-internal_studio-browse-kit_recommended-agent-results" */ "./RecommendedAgentResults"), { moduleId: "17d6aa0c", moduleName: "RecommendedAgentResults", modulePath: "../platform/packages/ai-mate/studio-agent-browse/src/entrypoints/recommended/RecommendedAgentResults.tsx", priority: "interaction", moduleId: 'studio-browse-kit_recommended-agent-results',
		}),
	getPreloadProps: ({ cloudId, product, fetchPolicy = 'store-or-network' }) => {
		if (expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
			const resolvedProduct = resolveProduct(product);
			return {
				queries: {
					codingAgentsQuery: {
						parameters: codingAgentsQuery,
						variables: { cloudId },
						options: { fetchPolicy },
					},
					recommendedAgentResultsQuery: {
						parameters: RecommendedAgentResultsQuery,
						variables: {
							cloudId,
							cloudIdString: cloudId,
							contextAri: `ari:cloud:jira::site/${cloudId}`,
							first: UPLIFT_TEMPLATE_PAGE_SIZE,
							useJiraRecommended:
								resolvedProduct !== 'confluence' && resolvedProduct !== 'jira-service-management',
							templateProduct: resolveTemplateProduct(product),
						},
						options: { fetchPolicy },
					},
				},
				entryPoints: {},
				extraProps: { product },
			};
		}

		const recentAgentIds = getRecentAgents(cloudId);
		const externalReferences =
			OOTB_AGENTS_BY_PRODUCT[product as keyof typeof OOTB_AGENTS_BY_PRODUCT] ??
			OOTB_AGENTS_BY_PRODUCT.default;
		const [ootb1 = '', ootb2 = '', ootb3 = ''] = externalReferences;

		return {
			queries: {
				legacyRecommendedAgentResultsQuery: {
					parameters: LegacyRecommendedAgentResultsQuery,
					variables: {
						cloudIdString: cloudId,
						agentsPerSource: RECOMMENDED_SLOT_COUNT,
						first: TEMPLATE_PAGE_SIZE,
						hasRecentAgents: recentAgentIds.length > 0,
						ids: recentAgentIds,
						product,
						templateProduct: resolveTemplateProduct(product),
						ootb1,
						ootb2,
						ootb3,
						hasOotb1: Boolean(ootb1),
						hasOotb2: Boolean(ootb2),
						hasOotb3: Boolean(ootb3),
					},
					options: { fetchPolicy },
				},
			},
			entryPoints: {},
			extraProps: { product },
		};
	},
});
